import React, { useEffect, useState } from "react";
import {
  Checkbox,
  DatePicker,
  Input,
  Radio,
  Select,
  Space,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { fmtDay } from "../institutionMerge.constants";

const { Text } = Typography;

/** `referencesBy` entries (model.field) → [singular, plural] French nouns for « N prestations (demandeur) ». */
export const REF_LABELS = {
  "Prestation.applicant": ["prestation (demandeur)", "prestations (demandeur)"],
  "Prestation.creator": ["prestation (créateur)", "prestations (créateur)"],
  "Prestation.intervenant": [
    "prestation (intervenant)",
    "prestations (intervenant)",
  ],
  "Prestation.interprete": [
    "prestation (interprète)",
    "prestations (interprète)",
  ],
  "Prestation.control1.user": [
    "prestation (contrôle)",
    "prestations (contrôle)",
  ],
  "Prestation.control2.user": [
    "prestation (contrôle)",
    "prestations (contrôle)",
  ],
  "Prestation.cancel.cancelBy": [
    "prestation (annulation)",
    "prestations (annulation)",
  ],
  "Prestation.interpretesStatus.interprete": [
    "prestation (statut interprète)",
    "prestations (statut interprète)",
  ],
  "Bill.downloaded.user": [
    "téléchargement de facture",
    "téléchargements de facture",
  ],
  "Address.user": ["adresse", "adresses"],
  "Schedule.user": ["disponibilités", "disponibilités"],
};

export const refLine = r => {
  const l = REF_LABELS[`${r.model}.${r.field}`];
  return l
    ? `${r.count} ${r.count > 1 ? l[1] : l[0]}`
    : `${r.count} ${r.model}.${r.field}`;
};

/** Value lists for the « Autre… » controls. The server sends `options` on a field when it has some; these are the fallbacks (User schema). */
export const ROLE_OPTIONS = [
  "responsable",
  "interprete",
  "institution",
  "mandant",
  "permanance",
  "admin",
];
export const STATUS_OPTIONS = ["active", "inactive", "archived"];
export const SUBROLE_OPTIONS = [
  "interprete",
  "institution",
  "mandant",
  "permanance",
  "admin",
  "institutionIntervenant",
  "institutionAdmin",
  "institutionFinance",
  "institutionDemander",
];
const ENUM_FALLBACK = {
  role: ROLE_OPTIONS,
  status: STATUS_OPTIONS,
  subRoles: SUBROLE_OPTIONS,
};
const enumOptions = f =>
  (f.options || ENUM_FALLBACK[f.path] || []).map(o =>
    typeof o === "string" ? { value: o, label: o } : o,
  );

export const isEmpty = v =>
  v === null ||
  v === undefined ||
  v === "" ||
  (Array.isArray(v) && v.length === 0);

/** Comparable form: every empty form collapses to null, booleans to true/false, arrays sorted, dates to ISO. */
export const normalise = (v, f = {}) => {
  if (f.kind === "boolean") return !!v;
  if (isEmpty(v)) return null;
  if (Array.isArray(v)) return v.map(String).sort();
  if (f.kind === "date") {
    const d = new Date(v);
    return isNaN(d) ? String(v) : d.toISOString();
  }
  if (typeof v === "string") return v.trim() || null;
  return v;
};
export const keyOf = (v, f) => JSON.stringify(normalise(v, f));

/** The "empty" value the server expects for a kind — never send `undefined`, it would drop the decision. */
export const emptyFor = kind =>
  ["multienum", "array", "refs"].includes(kind)
    ? []
    : kind === "boolean"
    ? false
    : ["ref", "date", "account"].includes(kind)
    ? null
    : "";

/** What is sent when the operator picks an existing value: the raw value, or the explicit empty form. */
export const saveForm = (v, f = {}) =>
  f.kind === "boolean" ? !!v : isEmpty(v) ? emptyFor(f.kind) : v;

/** Union (ordered, unique) of an array field over every account of the group. */
export const unionOf = (accounts, path) => {
  const out = [];
  for (const a of accounts) {
    const xs = (a.fields || {})[path];
    for (const v of Array.isArray(xs) ? xs : isEmpty(xs) ? [] : [xs])
      if (!out.includes(String(v))) out.push(String(v));
  }
  return out;
};

export const credentialsText = a =>
  a.hasPassword === false
    ? "aucun mot de passe"
    : `mot de passe défini · ${
        a.lastLogin
          ? `dernière connexion ${fmtDay(a.lastLogin)}`
          : "jamais connecté"
      }`;

/** Render a field value for display. ctx = { refNames, accounts }. */
export function showValue(v, f = {}, ctx = {}) {
  const names = ctx.refNames || {};
  if (f.kind === "boolean") return v ? "oui" : "non";
  if (isEmpty(v)) return <Text type="secondary">—</Text>;
  switch (f.kind) {
    case "date":
      return fmtDay(v);
    case "ref":
      return names[v] || String(v);
    case "refs":
      return (Array.isArray(v) ? v : [v])
        .map(id => names[id] || String(id))
        .join(", ");
    case "multienum":
      return (
        <Space size={4} wrap>
          {(Array.isArray(v) ? v : [v]).map(s => (
            <Tag key={s} style={{ marginRight: 0 }}>
              {s}
            </Tag>
          ))}
        </Space>
      );
    case "image":
      return (
        <Text ellipsis style={{ maxWidth: 220 }} title={String(v)}>
          {String(v)
            .split("/")
            .pop()}
        </Text>
      );
    case "account": {
      const a = (ctx.accounts || []).find(x => x._id === v);
      return a ? `${credentialsText(a)} — ${a.institution.name}` : String(v);
    }
    default:
      if (typeof v === "boolean") return v ? "oui" : "non";
      if (Array.isArray(v)) return v.join(", ");
      if (typeof v === "object") return JSON.stringify(v);
      return String(v);
  }
}

/** Text saved on blur / Enter — one PUT per edit, never per keystroke. */
function TextDraft({ value, kind, disabled, onSave }) {
  const str = v => (v === undefined || v === null ? "" : String(v));
  const [draft, setDraft] = useState(str(value));
  useEffect(() => setDraft(str(value)), [value]);
  const commit = () => {
    const v = draft.trim();
    if (v === "" && str(value) === "") return; // nothing typed: no accidental "empty" decision
    if (v === str(value)) return;
    const n = Number(v);
    onSave(kind === "number" && v !== "" && !isNaN(n) ? n : v);
  };
  return (
    <Input
      disabled={disabled}
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onPressEnter={e => e.target.blur()}
      placeholder="valeur personnalisée"
      style={{ maxWidth: 360 }}
    />
  );
}

/**
 * The « Après » control of one differing field: a radio per distinct "before" value
 * (the server's suggestion is tagged but never pre-checked — an explicit click decides),
 * plus « Autre… » revealing a control by kind. `decided` is the saved AFTER value (undefined = pending).
 */
export function AfterControl({
  f,
  accounts,
  decided,
  suggestion,
  disabled,
  onSave,
  refNames,
  principalOptions,
}) {
  const [otherOpen, setOtherOpen] = useState(false);
  const ctx = { refNames: refNames || {}, accounts };
  const kOf = v =>
    f.kind === "account"
      ? v === undefined || v === null
        ? null
        : String(v)
      : keyOf(v, f);

  const union = f.kind === "refs" ? unionOf(accounts, f.path) : [];
  const options = [];
  if (f.kind === "account") {
    for (const a of accounts)
      options.push({
        key: a._id,
        value: a._id,
        label: (
          <>
            mot de passe du compte {a.institution.name}{" "}
            <Text type="secondary">— {credentialsText(a)}</Text>
          </>
        ),
      });
  } else {
    const byKey = new Map();
    for (const a of accounts) {
      const v = (a.fields || {})[f.path];
      const k = keyOf(v, f);
      if (!byKey.has(k))
        byKey.set(k, { key: k, value: saveForm(v, f), accounts: [] });
      byKey.get(k).accounts.push(a);
    }
    options.push(...byKey.values());
    if (f.kind === "refs") {
      const k = keyOf(union, f);
      if (!byKey.has(k))
        options.push({ key: k, value: union, accounts: [], union: true });
    }
  }
  const suggestedKey = suggestion === undefined ? null : kOf(suggestion);
  const decidedKey = decided === undefined ? null : kOf(decided);
  const known = decidedKey !== null && options.some(o => o.key === decidedKey);
  const isOther = otherOpen || (decided !== undefined && !known);
  const custom = known ? undefined : decided; // only a custom decision feeds the « Autre… » control
  const enumOpts = enumOptions(f);
  const allowOther =
    !["account", "image", "boolean"].includes(f.kind) &&
    (f.kind !== "ref" || !!principalOptions) &&
    (!["enum", "multienum"].includes(f.kind) || enumOpts.length > 0);

  const pick = key => {
    if (key === "__other__") return setOtherOpen(true);
    setOtherOpen(false);
    onSave(options.find(o => o.key === key).value);
  };

  const customControl = () => {
    switch (f.kind) {
      case "text":
      case "number":
        return (
          <TextDraft
            value={custom}
            kind={f.kind}
            disabled={disabled}
            onSave={onSave}
          />
        );
      case "date":
        return (
          <DatePicker
            disabled={disabled}
            format="DD.MM.YYYY"
            value={custom ? dayjs(custom) : null}
            onChange={v => onSave(v ? v.startOf("day").toISOString() : null)}
          />
        );
      case "enum":
        return (
          <Select
            disabled={disabled}
            style={{ width: 240 }}
            value={custom === undefined ? undefined : custom}
            onChange={onSave}
            options={enumOpts}
            placeholder="choisir…"
          />
        );
      case "multienum":
        return (
          <Select
            disabled={disabled}
            mode="multiple"
            style={{ minWidth: 320 }}
            value={Array.isArray(custom) ? custom : []}
            onChange={onSave}
            options={enumOpts}
            placeholder="choisir…"
          />
        );
      case "array":
        return (
          <Select
            disabled={disabled}
            mode="tags"
            style={{ minWidth: 320 }}
            value={Array.isArray(custom) ? custom : []}
            onChange={onSave}
            options={unionOf(accounts, f.path).map(v => ({
              value: v,
              label: v,
            }))}
            tokenSeparators={[","]}
            placeholder="saisir…"
          />
        );
      case "ref":
        return (
          <Select
            disabled={disabled}
            showSearch
            allowClear
            optionFilterProp="label"
            style={{ minWidth: 320 }}
            value={custom === undefined || custom === null ? undefined : custom}
            onChange={v => onSave(v === undefined ? null : v)}
            options={principalOptions || []}
            placeholder="choisir…"
          />
        );
      case "refs":
        return (
          <Checkbox.Group
            disabled={disabled}
            value={Array.isArray(custom) ? custom : []}
            onChange={onSave}
            options={union.map(id => ({
              value: id,
              label: ctx.refNames[id] || id,
            }))}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div>
      <Radio.Group
        disabled={disabled}
        value={isOther ? "__other__" : decidedKey}
        onChange={e => pick(e.target.value)}
      >
        <Space direction="vertical" size={4}>
          {options.map(o => (
            <Radio key={o.key} value={o.key}>
              {o.label ||
                (isEmpty(o.value) && f.kind !== "boolean" ? (
                  <Text type="secondary">vide</Text>
                ) : (
                  showValue(o.value, f, ctx)
                ))}{" "}
              {o.union ? (
                <Text type="secondary">— réunion des deux comptes</Text>
              ) : o.accounts && o.accounts.length ? (
                <Text type="secondary">
                  — {o.accounts.map(a => a.institution.name).join(", ")}
                </Text>
              ) : null}
              {suggestedKey !== null && o.key === suggestedKey && (
                <Tag style={{ marginLeft: 6 }}>suggestion</Tag>
              )}
            </Radio>
          ))}
          {allowOther && <Radio value="__other__">Autre…</Radio>}
        </Space>
      </Radio.Group>
      {allowOther && isOther && (
        <div style={{ marginTop: 8 }}>{customControl()}</div>
      )}
    </div>
  );
}
